import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/**
 * Color de la barra del sistema, por tema.
 *
 * En una aplicación instalada, la franja de arriba del teléfono la pinta el
 * sistema operativo con el valor de <meta name="theme-color">. Si se queda
 * fijo, la mitad de las veces choca con la pantalla: barra blanca sobre una
 * aplicación oscura, o al revés. Por eso se cambia junto con el tema.
 */
const COLOR_DE_BARRA = { light: "#fefdf8", dark: "#15171b" };

/** Aplica el tema al documento y deja la barra del sistema a juego. */
const aplicar = (tema) => {
  document.documentElement.setAttribute("data-theme", tema);

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", COLOR_DE_BARRA[tema] || COLOR_DE_BARRA.dark);
};

const useThemeStore = create(
  persist(
    (set, get) => ({
      theme: 'system', // 'light', 'dark', or 'system'
      
      // Get the actual theme based on system preference
      getActualTheme: () => {
        const { theme } = get();
        if (theme === 'system') {
          return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }
        return theme;
      },
      
      // Set theme
      setTheme: (newTheme) => {
        set({ theme: newTheme });
        
        // Apply theme to document
        const actualTheme = newTheme === 'system' 
          ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
          : newTheme;
        
        aplicar(actualTheme);
      },
      
      // Initialize theme
      initializeTheme: () => {
        const { theme, getActualTheme } = get();
        const actualTheme = getActualTheme();
        aplicar(actualTheme);
        
        // Listen for system theme changes
        if (theme === 'system') {
          const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
          const handleChange = (e) => {
            aplicar(e.matches ? 'dark' : 'light');
          };
          mediaQuery.addEventListener('change', handleChange);
          
          // Return cleanup function
          return () => mediaQuery.removeEventListener('change', handleChange);
        }
      },
    }),
    {
      name: "theme-storage",
      storage: createJSONStorage(() => localStorage),
      version: 1,
    }
  )
);

// Initialize theme on load
if (typeof window !== 'undefined') {
  const { initializeTheme } = useThemeStore.getState();
  initializeTheme();
}

export default useThemeStore;